import { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/Button';
import { createLogger } from '@/lib/logger';
import { dataUrlToBlob } from '@/lib/image';

const log = createLogger('signature');

interface SignatureCaptureProps {
  /** Accent for the Accept button -- pickup (yellow) or delivery (green). */
  accent?: 'pickup' | 'delivery';
  onAccept: (blob: Blob) => Promise<void> | void;
  onCancel: () => void;
}

interface OrientationLockable extends ScreenOrientation {
  lock?: (orientation: string) => Promise<void>;
}

/**
 * Full-screen, forced-landscape signature takeover. Mirrors the native
 * SignatureCaptureScreen: a white canvas filling the screen with
 * Clear / Cancel / Accept, returning a PNG blob. Landscape orientation lock is
 * best-effort (works in installed/standalone PWAs that allow it; on iOS Safari
 * it is a no-op, an accepted platform limit).
 */
export function SignatureCapture({ accent = 'delivery', onAccept, onCancel }: SignatureCaptureProps) {
  const ref = useRef<SignatureCanvas | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [busy, setBusy] = useState(false);

  // Best-effort landscape lock (and release on exit).
  useEffect(() => {
    const orientation = (typeof screen !== 'undefined' ? screen.orientation : undefined) as
      | OrientationLockable
      | undefined;
    void (async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen().catch(() => undefined);
        }
        await orientation?.lock?.('landscape');
      } catch (err) {
        log.debug('orientation lock unavailable', err);
      }
    })();
    return () => {
      try {
        orientation?.unlock?.();
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      } catch {
        /* ignore */
      }
    };
  }, []);

  // Keep the canvas backing store sized to its container across resizes.
  useEffect(() => {
    const handler = () => {
      if (!ref.current || !containerRef.current) return;
      const data = ref.current.toData();
      const canvas = ref.current.getCanvas();
      const rect = containerRef.current.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      ref.current.fromData(data);
    };
    handler();
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  const clear = () => {
    ref.current?.clear();
    setIsEmpty(true);
  };

  const accept = async () => {
    if (!ref.current || ref.current.isEmpty()) return;
    setBusy(true);
    try {
      // Direct decode, not fetch(dataUrl): connect-src 'self' blocks data: fetches.
      const blob = dataUrlToBlob(ref.current.toDataURL('image/png'));
      await onAccept(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4-safe fixed inset-0 z-[70] flex flex-col gap-3 bg-brand-bg">
      <p className="text-center text-sm font-semibold text-brand-muted">Sign Below</p>
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden rounded-lg border-2 border-brand-border bg-white"
      >
        <SignatureCanvas
          ref={(c) => (ref.current = c)}
          penColor="black"
          canvasProps={{ className: 'w-full h-full touch-none' }}
          onEnd={() => setIsEmpty(false)}
        />
      </div>
      <div className="flex gap-3">
        <Button variant="outline" fullWidth disabled={isEmpty || busy} onClick={clear}>
          Clear
        </Button>
        <Button variant="ghost" fullWidth disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant={accent}
          fullWidth
          disabled={isEmpty || busy}
          onClick={() => void accept()}
        >
          Accept
        </Button>
      </div>
    </div>
  );
}
